import { useEffect, useRef, type ComponentProps, type ReactNode } from 'react'
import { Button, Input, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  messagesListTabChanged,
  openMessageThread,
} from '../../adviser-chat.slice'
import { selectMessagesListTab } from '../../adviser-chat.selectors'
import { useAdviserAnalytics } from '../../analytics'
import { useMessageThreads } from '../../hooks/useMessageThreads'
import type { MessageTab, MessageThreadCounts } from '../../lib/types'
import { isReplyReflected } from '../../lib/messageTabs'
import { MessageRow } from './MessageRow'
import { MessageTabs } from './MessageTabs'
import { MessagesEmptyState } from './MessagesEmptyState'

// hasHistory, not the row count and not the counts: a customer whose threads
// are all resolved is not a new user.
const emptyVariantFor = (
  searching: boolean,
  hasHistory: boolean
): ComponentProps<typeof MessagesEmptyState>['variant'] => {
  if (searching) return 'noResults'
  return hasHistory ? 'filtered' : 'firstRun'
}

interface MessagesViewProps {
  initialTab?: MessageTab
  restoreTab?: boolean
  postedReplyTicketId?: number
  initialQuery?: string
  /** Counts the tool already resolved, so the chips do not start at zero. */
  initialCounts?: MessageThreadCounts
}

export const MessagesView = ({
  initialTab,
  restoreTab = false,
  postedReplyTicketId,
  initialQuery,
  initialCounts,
}: MessagesViewProps) => {
  const dispatch = useAppDispatch()
  const rememberedTab = useReselector(selectMessagesListTab)
  const startTabRef = useRef(
    restoreTab ? (rememberedTab ?? initialTab) : initialTab
  )
  const {
    threads,
    counts,
    hasHistory,
    scope,
    resolvedTab,
    search,
    setSearch,
    selectTab,
    loadOlder,
    hasMore,
    loading,
    loadingOlder,
    error,
    retry,
  } = useMessageThreads({
    initialTab: startTabRef.current,
    initialQuery,
    initialCounts,
    isSettled:
      postedReplyTicketId === undefined
        ? undefined
        : (page, tab) => isReplyReflected(page, tab, postedReplyTicketId),
  })

  useEffect(() => {
    dispatch(messagesListTabChanged(resolvedTab))
  }, [dispatch, resolvedTab])

  const { trackMessagesListViewed } = useAdviserAnalytics()
  const viewTrackedRef = useRef(false)
  useEffect(() => {
    if (loading || error || viewTrackedRef.current) return
    viewTrackedRef.current = true
    trackMessagesListViewed({ tab: resolvedTab, scope })
  }, [loading, error, resolvedTab, scope, trackMessagesListViewed])

  const isSearching = scope === 'search'
  const hasQuery = search.trim() !== ''

  const renderList = (): ReactNode => {
    if (error) {
      return (
        <div className="flex flex-col items-center gap-2 border-t border-neutral-100 py-10">
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            We could not load your messages.
          </Text>
          <Button variant="secondary" size="sm" onClick={retry}>
            Try again
          </Button>
        </div>
      )
    }
    if (loading) {
      return (
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.MUTED}
          className="border-t border-neutral-100 py-10 text-center"
        >
          Loading your messages…
        </Text>
      )
    }
    if (threads.length === 0) {
      return (
        <div className="border-t border-neutral-100 p-4">
          <MessagesEmptyState
            variant={emptyVariantFor(isSearching || hasQuery, hasHistory)}
            onSeeAll={() => selectTab('all')}
          />
        </div>
      )
    }
    return (
      <>
        {threads.map((thread) => (
          <MessageRow
            key={thread.ticketId}
            thread={thread}
            showStatusChip={isSearching || resolvedTab === 'all'}
            onOpen={(ticketId) => dispatch(openMessageThread(ticketId))}
          />
        ))}
        {hasMore && (
          <Button
            variant="secondary"
            size="sm"
            disabled={loadingOlder}
            onClick={loadOlder}
            className="my-3 self-center"
          >
            Load older
          </Button>
        )}
      </>
    )
  }

  const hasRows = !error && !loading && threads.length > 0

  return (
    <div className="my-2 flex flex-col gap-3">
      <MessageTabs
        activeTab={isSearching ? null : resolvedTab}
        counts={counts}
        onSelect={selectTab}
      />

      <Input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search messages"
        aria-label="Search messages"
        className="shrink-0"
      />

      <div
        data-testid="messages-inline-list"
        className={mtc(
          'flex flex-col',
          hasRows
            ? 'gap-2'
            : 'overflow-hidden rounded-2xl border border-neutral-100 bg-neutral-0 [&>*:first-child]:border-t-0'
        )}
      >
        {renderList()}
      </div>
    </div>
  )
}
